import { createSlice } from '@reduxjs/toolkit';

// Errors are kept whole ({ status, code, message }) because the pages route on them:
// snapshot_not_received is an empty state, a 403 is the access message.
const read = () => ({ data: null, loading: false, error: null });
const list = () => ({
  items: [],
  total: 0,
  page: 1,
  pageSize: 50,
  query: {},
  loading: false,
  error: null,
});
const detail = () => ({ ficr: null, data: null, loading: false, error: null });

const initialState = {
  // Everything but the three lists; also the header's clock on every page.
  line: read(),
  devices: list(),
  device: detail(),
  chargers: list(),
  charger: detail(),
  sets: list(),
  quality: read(),
  benches: read(),
};

const startRead = (slot) => {
  slot.loading = true;
  slot.error = null;
};
const readOk = (slot, data) => {
  slot.data = data;
  slot.loading = false;
};
const readFailed = (slot, error) => {
  slot.loading = false;
  slot.error = error;
};

// A list request replaces the rows: the old page under a new filter reads as a wrong answer.
const startList = (slot, query) => {
  slot.query = query || {};
  slot.items = [];
  slot.loading = true;
  slot.error = null;
};
const listOk = (slot, payload) => {
  slot.items = payload.items;
  slot.total = payload.total;
  slot.page = payload.page;
  slot.pageSize = payload.pageSize;
  slot.loading = false;
};
const listFailed = (slot, error) => {
  slot.items = [];
  slot.total = 0;
  slot.loading = false;
  slot.error = error;
};

const startDetail = (slot, ficr) => {
  // Drop the old unit first, or it reads under the new unit's heading.
  slot.ficr = ficr;
  slot.data = null;
  slot.loading = true;
  slot.error = null;
};
const detailOk = (slot, payload) => {
  if (payload.ficr !== slot.ficr) return;
  slot.data = payload.data;
  slot.loading = false;
};
const detailFailed = (slot, payload) => {
  if (payload.ficr !== slot.ficr) return;
  slot.loading = false;
  slot.error = payload.error;
};

const productionSlice = createSlice({
  name: 'production',
  initialState,
  reducers: {
    getLineAction: (state) => startRead(state.line),
    getLineSuccess: (state, action) => readOk(state.line, action.payload),
    getLineFailure: (state, action) => readFailed(state.line, action.payload),

    getDevicesAction: (state, action) => startList(state.devices, action.payload),
    getDevicesSuccess: (state, action) => listOk(state.devices, action.payload),
    getDevicesFailure: (state, action) => listFailed(state.devices, action.payload),

    getDeviceAction: (state, action) => startDetail(state.device, action.payload),
    getDeviceSuccess: (state, action) => detailOk(state.device, action.payload),
    getDeviceFailure: (state, action) => detailFailed(state.device, action.payload),

    getChargersAction: (state, action) => startList(state.chargers, action.payload),
    getChargersSuccess: (state, action) => listOk(state.chargers, action.payload),
    getChargersFailure: (state, action) => listFailed(state.chargers, action.payload),

    getChargerAction: (state, action) => startDetail(state.charger, action.payload),
    getChargerSuccess: (state, action) => detailOk(state.charger, action.payload),
    getChargerFailure: (state, action) => detailFailed(state.charger, action.payload),

    getSetsAction: (state, action) => startList(state.sets, action.payload),
    getSetsSuccess: (state, action) => listOk(state.sets, action.payload),
    getSetsFailure: (state, action) => listFailed(state.sets, action.payload),

    getQualityAction: (state) => startRead(state.quality),
    getQualitySuccess: (state, action) => readOk(state.quality, action.payload),
    getQualityFailure: (state, action) => readFailed(state.quality, action.payload),

    getBenchesAction: (state) => startRead(state.benches),
    getBenchesSuccess: (state, action) => readOk(state.benches, action.payload),
    getBenchesFailure: (state, action) => readFailed(state.benches, action.payload),
  },
});

export const {
  getLineAction,
  getLineSuccess,
  getLineFailure,
  getDevicesAction,
  getDevicesSuccess,
  getDevicesFailure,
  getDeviceAction,
  getDeviceSuccess,
  getDeviceFailure,
  getChargersAction,
  getChargersSuccess,
  getChargersFailure,
  getChargerAction,
  getChargerSuccess,
  getChargerFailure,
  getSetsAction,
  getSetsSuccess,
  getSetsFailure,
  getQualityAction,
  getQualitySuccess,
  getQualityFailure,
  getBenchesAction,
  getBenchesSuccess,
  getBenchesFailure,
} = productionSlice.actions;

export { productionSlice };
